import DateInput from "@/shared/components/DateInput";
import { ReactNode, useEffect, useMemo, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
// Import your existing DataTable
import { App_Url_New } from "@/shared/common/static";
import { DataTable } from "@/shared/components/DataTable/DataTable";
import SummaryMetricCard from "@/shared/components/SummaryMetricCard";
import { B2C_FEATURE_FLAGS } from "@/shared/config/header.config";
import { formatLocalizedCurrency, LOCALISATION_CONFIG } from "@/shared/config/localisation";
import {
  setCommissionRecords as listReducer,
  setCommissionDateRange,
  setCommissionInsurer,
  setCommissionStatuses,
} from "@/shared/redux/reducers/commission-ledger";
import { usePosterReducers } from "@/shared/redux/store/useGetData";
import { textFormatter } from "@/shared/utils/textFormatter";
import { CheckCircle2, CircleUser, Clock3, FileText, Wallet } from "lucide-react";
import { Status } from "../Policies/components/Status";
import { CommissionRecord } from "./types";

const formatCurrency = (amount: number) => formatLocalizedCurrency(amount);

const toNumber = (value: unknown, fallback = 0) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : fallback;
};

const formatIssueDate = (dateValue?: string) => {
  if (!dateValue) return "-";

  const parsed = new Date(dateValue);
  if (Number.isNaN(parsed.getTime())) return "-";

  return parsed.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

type ApiCommissionLedgerRow = {
  id?: string | number;
  insurer_id?: string | number;
  policy_no?: string | number;
  insurer?: string;
  product?: string;
  customer?: string;
  premium?: number | string;
  rate?: number | string;
  commission?: number | string;
  tax_type?: number | string;
  exp_tds?: number | string;
  type?: string;
  issue_date?: string;
  status?: string;
  invoice?: string;
};

const mapApiRowToCommissionRecord = (row: ApiCommissionLedgerRow): CommissionRecord => {
  const normalizedStatus = String(row.status).toUpperCase();
  const allowedStatuses = new Set(["PAID", "UNBILLED", "RECONCILED", "INVOICED"]);
  const taxAmount = toNumber(row.tax_type);

  return {
    id: row.id ?? "",
    policyNo: String(row.policy_no ?? "-"),
    insurer: row.insurer || "-",
    product: row.product || "-",
    customer: row.customer || "-",
    premium: toNumber(row.premium),
    rate: toNumber(row.rate),
    commission: toNumber(row.commission),
    tax_type: toNumber(row.tax_type),
    expTds: toNumber(row.exp_tds),
    type: row.type || "-",
    issueDate: formatIssueDate(row.issue_date),
    status: (allowedStatuses.has(normalizedStatus)
      ? normalizedStatus
      : "UNBILLED") as CommissionRecord["status"],
    invoice: row.invoice || "-",
  };
};

type CommissionDateFilterForm = {
  from_date: string;
  to_date: string;
};

export default function CommissionLedgerPage() {
  const {
    commissionLedger: { commissionRecords, statuses, insurers, dateRanges },
  } = usePosterReducers();

  const { control, setValue } = useForm<CommissionDateFilterForm>({
    defaultValues: {
      from_date: "",
      to_date: "",
    },
  });

  const [controlledFilters, setControlledFilters] = useState<
    Record<string, string | number | null | undefined>
  >({});

  const selectedDateRange = controlledFilters.date_range;
  const fromDate = useWatch({ control, name: "from_date" });
  const toDate = useWatch({ control, name: "to_date" });

  const records = useMemo(
    () =>
      (commissionRecords?.data || []).map((row: ApiCommissionLedgerRow) =>
        mapApiRowToCommissionRecord(row)
      ),
    [commissionRecords?.data]
  );

  const summaryCards = useMemo(() => {
    const iconByLabel: Record<string, ReactNode> = {
      "Expected Commission": <Wallet className="h-5 w-5" />,
      Unbilled: <Clock3 className="h-5 w-5" />,
      Invoiced: <FileText className="h-5 w-5" />,
      "Collected (Net)": <CheckCircle2 className="h-5 w-5" />,
    };

    const defaultSummary = [
      {
        label: "Expected Commission",
        value: 0,
        theme: "primary" as const,
        icon: iconByLabel["Expected Commission"],
      },
      { label: "Unbilled", value: 0, theme: "warning" as const, icon: iconByLabel.Unbilled },
      { label: "Invoiced", value: 0, theme: "danger" as const, icon: iconByLabel.Invoiced },
      {
        label: "Collected (Net)",
        value: 0,
        theme: "success" as const,
        icon: iconByLabel["Collected (Net)"],
      },
    ];

    const themeByLabel: Record<string, "primary" | "warning" | "danger" | "success"> = {
      "Expected Commission": "primary",
      Unbilled: "warning",
      Invoiced: "danger",
      "Collected (Net)": "success",
    };

    const apiSummary = Array.isArray(commissionRecords?.summary) ? commissionRecords.summary : [];
    if (apiSummary.length === 0) return defaultSummary;

    return apiSummary.map((item) => ({
      label: item?.label || "-",
      value: Number(item?.value || 0),
      theme: themeByLabel[item?.label || ""] || "primary",
      icon: iconByLabel[item?.label || ""],
    }));
  }, [commissionRecords?.summary]);

  // --- DataTable Configurations ---
  const taxLabel = LOCALISATION_CONFIG.tax?.label || "Tax";

  const tableHeaders = [
    "Policy No",
    "Insurer",
    "Product",
    "Customer",
    "Premium",
    "Rate",
    "Commission",
    taxLabel,
    "Issue Date",
    "Status",
    "Invoice",
  ];

  const statusOptions = useMemo(
    () =>
      (statuses || [])
        .map((item) => ({
          value: item?.id,
          label: item?.name,
        }))
        .filter((option) => option.value !== null && option.value !== undefined),
    [statuses]
  );

  const dateRangeOptions = useMemo(
    () =>
      (dateRanges || [])
        .map((item) => ({
          value: item?.id,
          label: item?.name,
        }))
        .filter((option) => option.value !== null && option.value !== undefined),
    [dateRanges]
  );

  const insurerOptionsById = useMemo(
    () =>
      (insurers || [])
        .map((item) => ({
          value: item?.id,
          label: item?.name,
        }))
        .filter((option) => option.value !== null && option.value !== undefined),
    [insurers]
  );

  const customDateRangeValue = useMemo(() => {
    const customOption = (dateRangeOptions || []).find((option) =>
      String(option.label || "")
        .replace(/\s+/g, "_")
        .toUpperCase()
        .includes("CUSTOM")
    );

    return customOption?.value ?? "CUSTOM_DATE_RANGE";
  }, [dateRangeOptions]);

  const isCustomDateRangeSelected =
    String(selectedDateRange ?? "").toUpperCase() === String(customDateRangeValue).toUpperCase() ||
    String(selectedDateRange ?? "").toUpperCase() === "CUSTOM_DATE_RANGE";

  useEffect(() => {
    if (!isCustomDateRangeSelected) {
      setValue("from_date", "");
      setValue("to_date", "");

      setControlledFilters((prev) => ({
        ...prev,
        from_date: undefined,
        to_date: undefined,
      }));
      return;
    }

    setControlledFilters((prev) => ({
      ...prev,
      from_date: fromDate || undefined,
      to_date: toDate || undefined,
    }));
  }, [fromDate, toDate, isCustomDateRangeSelected, setValue]);

  const filters = useMemo(() => {
    const baseFilters = [
      {
        name: "status",
        valueKey: "status",
        requests: {
          method: "GET",
          url: App_Url_New.ApiEndPoint.commissionStatus,
          reducer: setCommissionStatuses,
        },
        options: statusOptions,
        placeholder: "Select Status",
        width: "170px",
      },
      {
        name: "date_range",
        valueKey: "date_range",
        requests: {
          method: "GET",
          url: App_Url_New.ApiEndPoint.commissionDateRange,
          reducer: setCommissionDateRange,
        },
        options: dateRangeOptions || [],
        placeholder: "Select Date Range",
        width: "170px",
      },
    ];

    if (B2C_FEATURE_FLAGS.isOCEnvironment) {
      return baseFilters;
    }

    return [
      {
        name: "insurer_id",
        valueKey: "insurer_id",
        options: insurerOptionsById,
        placeholder: "Select Insurer",
        requests: {
          method: "GET",
          url: App_Url_New.ApiEndPoint.commissionInsurer,
          reducer: setCommissionInsurer,
        },
        width: "170px",
      },
      ...baseFilters,
    ];
  }, [insurerOptionsById, statusOptions, dateRangeOptions]);

  const handleFilterChange = (name: string, value: string | number) => {
    setControlledFilters((prev) => {
      const nextFilters = {
        ...prev,
        [name]: value === "" ? undefined : value,
      };

      if (name === "date_range") {
        const isCustomValue =
          String(value ?? "").toUpperCase() === String(customDateRangeValue).toUpperCase() ||
          String(value ?? "").toUpperCase() === "CUSTOM_DATE_RANGE";

        if (!isCustomValue) {
          nextFilters.from_date = undefined;
          nextFilters.to_date = undefined;
        }
      }

      return nextFilters;
    });
  };

  const customDateControls = isCustomDateRangeSelected ? (
    <div className="flex items-end gap-3">
      <DateInput
        control={control}
        name="from_date"
        label="From"
        placeholder="From date"
        className="w-[170px] [&>label]:hidden gap-0"
      />

      <DateInput
        control={control}
        name="to_date"
        label="To"
        placeholder="To date"
        className="w-[170px] [&>label]:hidden gap-0"
        min={fromDate || undefined}
      />
    </div>
  ) : null;

  const shouldFetchCommissionLedger = (requestBody: any) => {
    const filters = requestBody?.filters || {};
    const selectedRange = filters?.date_range;
    const isCustomRange =
      String(selectedRange ?? "").toUpperCase() === String(customDateRangeValue).toUpperCase() ||
      String(selectedRange ?? "").toUpperCase() === "CUSTOM_DATE_RANGE";

    if (!isCustomRange) return true;

    return Boolean(filters?.from_date && filters?.to_date);
  };

  // Provide the custom rendering for the row contents (the <td> elements)
  const renderTableRow = (row: CommissionRecord) => {
    return (
      <>
        <td className="px-4 py-3 font-medium">{row.policyNo}</td>
        <td className="px-4 py-3 max-w-[150px] truncate" title={row.insurer}>
          {row.insurer}
        </td>
        <td className="px-4 py-3 max-w-[200px] truncate " title={row.product}>
          {textFormatter(row.product)}
        </td>
        <td className="max-w-[200px] px-4 py-3">
          <div className="flex items-center gap-2">
            <CircleUser className="h-4 w-4 shrink-0 text-theme-text-soft" />

            <span
              className="min-w-0 flex-1 truncate text-sm text-theme-text"
              title={row.customer || "-"}
            >
              {textFormatter(row.customer || "-")}
            </span>
          </div>
        </td>
        <td className="px-4 py-3 text-center">{formatCurrency(row.premium)}</td>
        <td className="px-4 py-3 text-center">{row.rate?.toFixed(1)}%</td>
        <td className="px-4 py-3 text-center">{formatCurrency(row.commission)}</td>
        <td className="px-4 py-3 text-center">{formatCurrency(row.tax_type)}</td>
        {/* <td className="px-4 py-3 text-center text-theme-danger">{formatCurrency (row.expTds)}</td> */}

        <td className="px-4 py-3 max-w-[200px] truncate" title={row.issueDate}>
          {row.issueDate}
        </td>
        <td className="px-4 py-3">
          <Status status={textFormatter(row.status)} />
        </td>
        <td className="px-4 py-3 text-center max-w-[170px] truncate" title={row.invoice}>
          {row.invoice}
        </td>
      </>
    );
  };

  return (
    <>
      {/* Summary Metrics */}
      <div className="mb-3 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {summaryCards.map((item) => (
          <SummaryMetricCard
            key={item.label}
            title={item.label}
            value={formatCurrency(item.value)}
            theme={item.theme}
            icon={item.icon}
          />
        ))}
      </div>

      {/* Reusable Data Table */}
      <DataTable
        headers={tableHeaders}
        rows={records}
        url={App_Url_New.ApiEndPoint.commissionLedger}
        renderRow={renderTableRow}
        listReducer={listReducer}
        filters={filters as any}
        controlledFilters={controlledFilters}
        onFilterChange={handleFilterChange}
        extraControls={customDateControls}
        shouldFetch={shouldFetchCommissionLedger}
        searchPlaceholder="Search by Policy No, Insurer, Product etc..."
        isPagination={true}
        pageSize={10}
        totalCount={commissionRecords?.total || 0}
        sortable={true}
        staticHeight={60}
        nonSortableColumns={["Issue Date", "GST", "Policy No"]}
        // showSerialNumber={true}
        searchWidth={"30%"}
      />
    </>
  );
}
